import { useState } from 'react';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';

import Button from '@/components/Button';
import Stack from '@/components/Stack';
import { Small } from '@/components/Typography';
import {
  experimentsAPI,
  extractExperimentsApiError,
  CohortFilter,
  ExperimentDetail,
  ExperimentPayload,
  ExperimentVariant,
} from '@/services/experimentsAPI';

export type Props = {
  flagKey: string;
  // Present -> edit mode (pre-filled from the experiment's full detail
  // payload, which the list view doesn't carry). Absent -> create mode.
  experiment?: ExperimentDetail | null;
  onClose: () => void;
  onSaved: (experiment: ExperimentDetail) => void;
};

// Splits on commas, whitespace AND semicolons — ops users paste ids in all
// three shapes. Returns null when any token is unparseable: a comma-only
// split used to reduce "1024 2048" to [] and SEND it, and an empty
// user_ids_in matches EVERY user in graphy's CohortMatcher (blank passes) —
// silently turning a hand-picked cohort into a 100% rollout. Unparseable
// input must block Save, never degrade to a broader cohort.
const parseUserIds = (raw: string): number[] | null => {
  const tokens = raw.split(/[\s,;]+/).filter(Boolean);
  const ids = tokens.map(Number);
  if (ids.some((n) => !Number.isFinite(n))) return null;
  return ids;
};

// Server-editable statuses (Experiment#update returns 422 otherwise — see
// api/admin/experiments_controller.rb#update). Lifecycle transitions
// (start/pause/complete) are separate POST endpoints, not this form.
const EDITABLE_STATUSES = ['draft', 'paused'];

const ExperimentModal = ({ flagKey, experiment, onClose, onSaved }: Props) => {
  const isEdit = Boolean(experiment);
  // The experiment key is the hash-bucketing salt; graphy drops it from
  // permitted params once started_at is set, so mutating it client-side
  // afterward would silently no-op. Disable rather than hide it — and, since
  // the field stays editable for a draft/paused-never-started experiment,
  // keep sending it on save whenever it isn't locked (see handleSubmit)
  // rather than unconditionally omitting it in edit mode.
  const keyLocked = Boolean(experiment?.started_at);

  // Variants this form doesn't model (anything besides control/treatment).
  // Round-tripped untouched on save so editing a control/treatment weight
  // never silently deletes e.g. a holdout arm.
  const otherVariants: ExperimentVariant[] = (
    experiment?.variants ?? []
  ).filter((v) => v.key !== 'control' && v.key !== 'treatment');
  const otherVariantsWeight = otherVariants.reduce(
    (sum, v) => sum + v.weight,
    0,
  );

  // This form only edits a strict 2-arm control/treatment layout (any other
  // loaded variants are round-tripped read-only, above). An experiment
  // created via the API or seeded server-side may not have that shape at
  // all — e.g. `a: 50, b: 50` with no variant keyed 'control' or
  // 'treatment'. Defaulting controlWeight/treatmentWeight to 0/100 in that
  // case would fabricate inputs with no backing data: weightSum would
  // include the real otherVariants total plus two phantom arms, almost
  // always overshooting 100 with no editable field that explains why (a
  // silently-dead Save button). Detect the case up front and fall back to a
  // clear read-only message instead of guessing.
  const variantKeys = new Set((experiment?.variants ?? []).map((v) => v.key));
  const usesUnsupportedVariantShape =
    isEdit && (!variantKeys.has('control') || !variantKeys.has('treatment'));

  const readOnly =
    isEdit &&
    (!EDITABLE_STATUSES.includes(experiment!.status) ||
      usesUnsupportedVariantShape);

  const [key, setKey] = useState(experiment?.key ?? '');
  const [name, setName] = useState(experiment?.name ?? '');
  const [description, setDescription] = useState(experiment?.description ?? '');
  const [rolloutPercentage, setRolloutPercentage] = useState(
    experiment?.rollout_percentage ?? 100,
  );
  const [controlWeight, setControlWeight] = useState(
    experiment?.variants.find((v) => v.key === 'control')?.weight ?? 0,
  );
  const [treatmentWeight, setTreatmentWeight] = useState(
    experiment?.variants.find((v) => v.key === 'treatment')?.weight ?? 100,
  );
  const [userIdsIn, setUserIdsIn] = useState(
    // cohort_filter itself can be NULL on rows that predate the model
    // default (the builder emits the column verbatim) — chain all the way.
    (experiment?.cohort_filter?.user_ids_in ?? []).join(', '),
  );
  const [allowlistText, setAllowlistText] = useState(
    JSON.stringify(experiment?.allowlist_assignments ?? {}, null, 2),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mirrors Experiment#validate_variants (weights must sum to 100) so the
  // ops user gets instant feedback instead of a round-trip 422. Includes any
  // untouched other-variant weights (see otherVariants above) since those
  // count toward the same 100% total server-side.
  const weightSum = controlWeight + treatmentWeight + otherVariantsWeight;
  // graphy sums weights and applies rollout with .to_i: 50.5+49.5 passes a
  // float sum-to-100 check here then 422s server-side, and a 0.5% rollout
  // silently enrolls nobody (0.5.to_i == 0). Integers only, end to end.
  const weightsInteger =
    Number.isInteger(controlWeight) && Number.isInteger(treatmentWeight);
  const weightsValid = weightSum === 100 && weightsInteger;
  const rolloutValid =
    Number.isInteger(rolloutPercentage) &&
    rolloutPercentage >= 0 &&
    rolloutPercentage <= 100;

  // null = unparseable token(s); [] = deliberately blank field.
  const parsedUserIds = userIdsIn.trim() ? parseUserIds(userIdsIn) : [];
  const cohortError = parsedUserIds === null;

  let allowlistParsed: Record<string, string> = {};
  let allowlistError = false;
  try {
    const parsed = allowlistText.trim() ? JSON.parse(allowlistText) : {};
    // Any valid JSON parses — but graphy's strong params accept only a hash,
    // and silently DROP a non-hash value from the permitted params, so
    // "clearing" an allowlist with [] reported success while changing
    // nothing. Only a plain object may pass this gate.
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      allowlistError = true;
    } else {
      allowlistParsed = parsed;
    }
  } catch {
    allowlistError = true;
  }

  // No client validation previously existed for key/name — a blank name (or
  // a blank, still-editable key) round-tripped straight to a 422 with only
  // weight/allowlist feedback shown up front. keyLocked experiments never
  // send key at all (see handleSubmit), so an emptied-but-locked key field
  // can't actually break anything and isn't gated here.
  const keyValid = keyLocked || key.trim().length > 0;
  const nameValid = name.trim().length > 0;

  const canSave =
    weightsValid &&
    rolloutValid &&
    !cohortError &&
    !allowlistError &&
    keyValid &&
    nameValid &&
    !saving &&
    !readOnly;

  const handleSubmit = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);

    // Preserve cohort_filter keys this form doesn't model (registered_after/
    // before, is_worker, user_ids_not_in) — spread the loaded filter and
    // only touch user_ids_in, rather than replacing the whole hash.
    const cohortFilter: CohortFilter = { ...(experiment?.cohort_filter ?? {}) };
    if (userIdsIn.trim()) {
      // canSave already blocked null (unparseable) — never send a silently
      // emptied cohort.
      cohortFilter.user_ids_in = parsedUserIds ?? [];
    } else {
      delete cohortFilter.user_ids_in;
    }

    const payload: ExperimentPayload = {
      // Send the key whenever it's editable — including on a draft/paused
      // experiment being edited, not just on create — so a key change made
      // while it's still unlocked actually persists. Omitted once
      // keyLocked (started_at set), matching what the server accepts.
      ...(keyLocked ? {} : { key: key.trim() }),
      name: name.trim(),
      // Send '' (not undefined) so clearing an existing description
      // actually persists — `x: undefined` is dropped by JSON.stringify,
      // so the field would silently round-trip unchanged.
      description: description.trim(),
      rollout_percentage: rolloutPercentage,
      // Preserve the LOADED variant order, substituting only the edited
      // control/treatment weights. graphy's HashBucketer walks the variants
      // array in order accumulating weights, so rebuilding it as
      // [control, treatment, ...others] on an experiment stored in another
      // order silently remapped which hash buckets land in which arm for
      // every future enrollee — an invisible mid-experiment skew.
      variants: experiment
        ? experiment.variants.map((v) =>
            v.key === 'control'
              ? { ...v, weight: controlWeight }
              : v.key === 'treatment'
                ? { ...v, weight: treatmentWeight }
                : v,
          )
        : [
            { key: 'control', weight: controlWeight },
            { key: 'treatment', weight: treatmentWeight },
          ],
      cohort_filter: cohortFilter,
      allowlist_assignments: allowlistParsed,
    };

    try {
      const saved =
        isEdit && experiment
          ? await experimentsAPI.updateExperiment(
              flagKey,
              experiment.id,
              payload,
            )
          : await experimentsAPI.createExperiment(flagKey, payload);
      onSaved(saved);
      onClose();
    } catch (err: any) {
      setError(extractExperimentsApiError(err, 'Failed to save experiment'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? `Edit experiment: ${experiment!.name}` : 'New experiment'}
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        {usesUnsupportedVariantShape ? (
          <Alert severity="info">
            This experiment&apos;s variants (
            {(experiment?.variants ?? [])
              .map((v) => `${v.key}: ${v.weight}%`)
              .join(', ')}
            ) aren&apos;t a control/treatment pair, so weights can&apos;t be
            edited in this form. View-only below.
          </Alert>
        ) : (
          readOnly && (
            <Alert severity="info">
              This experiment is {experiment!.status} and can no longer be
              edited here. Use the Start / Pause / Complete actions to change
              its status.
            </Alert>
          )
        )}
        <TextField
          autoFocus={!isEdit}
          label="Experiment key"
          helperText="Hash-bucketing salt. Locked once the experiment has started."
          placeholder="e.g. preferred-staffing-modal-v1"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          disabled={readOnly || keyLocked}
          fullWidth
          size="small"
          sx={{ mt: 1 }}
        />
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={readOnly}
          fullWidth
          size="small"
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={readOnly}
          fullWidth
          multiline
          minRows={2}
          size="small"
        />
        <TextField
          label="Rollout percentage"
          type="number"
          value={rolloutPercentage}
          onChange={(e) => setRolloutPercentage(Number(e.target.value))}
          disabled={readOnly}
          error={!rolloutValid}
          helperText={
            rolloutValid
              ? undefined
              : 'Whole number 0–100 — graphy truncates decimals, so 0.5 would enroll nobody.'
          }
          inputProps={{ min: 0, max: 100, step: 1 }}
          size="small"
        />
        {!usesUnsupportedVariantShape && (
          <>
            <Stack gap={16}>
              <TextField
                label="Control weight"
                type="number"
                value={controlWeight}
                onChange={(e) => setControlWeight(Number(e.target.value))}
                disabled={readOnly}
                error={!Number.isInteger(controlWeight)}
                inputProps={{ min: 0, max: 100, step: 1 }}
                size="small"
              />
              <TextField
                label="Treatment weight"
                type="number"
                value={treatmentWeight}
                onChange={(e) => setTreatmentWeight(Number(e.target.value))}
                disabled={readOnly}
                error={!Number.isInteger(treatmentWeight)}
                inputProps={{ min: 0, max: 100, step: 1 }}
                size="small"
              />
            </Stack>
            {otherVariants.length > 0 && (
              <Small color="lighter">
                This experiment also has {otherVariants.length} other variant
                {otherVariants.length === 1 ? '' : 's'} not editable here (
                {otherVariants.map((v) => `${v.key}: ${v.weight}%`).join(', ')})
                — their weight counts toward the 100% total below and they are
                preserved as-is on save.
              </Small>
            )}
            {!weightsValid && (
              <Small color="lighter">
                All variant weights must sum to 100 (currently {weightSum}).
              </Small>
            )}
          </>
        )}
        <TextField
          label="Cohort: user_ids_in"
          error={cohortError}
          helperText={
            cohortError
              ? 'Unrecognized id in the list — only numeric ids separated by commas, spaces or semicolons. Not sent until fixed (a mangled cohort would silently target everyone).'
              : 'Ids separated by commas, spaces or semicolons. Leave blank to target everyone within rollout_percentage.'
          }
          placeholder="e.g. 1024, 2048, 4096"
          value={userIdsIn}
          onChange={(e) => setUserIdsIn(e.target.value)}
          disabled={readOnly}
          fullWidth
          size="small"
        />
        <TextField
          label="Allowlist assignments (JSON)"
          helperText='{ "user_id": "variant_key" } — forces specific users into a variant regardless of rollout/cohort.'
          value={allowlistText}
          onChange={(e) => setAllowlistText(e.target.value)}
          disabled={readOnly}
          error={allowlistError}
          fullWidth
          multiline
          minRows={3}
          size="small"
        />
      </DialogContent>
      <DialogActions>
        <Button
          a11yLabel="Cancel"
          label="Cancel"
          appearance="plain"
          onClick={onClose}
        />
        <Button
          a11yLabel="Save experiment"
          label="Save"
          isLoading={saving}
          disabled={!canSave}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ExperimentModal;
